import { eq } from "@-/com.npmjs.drizzle-orm";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import { useTraditionalCharactersEnabled } from "@-/dev.improve.haniverse.lib.posthog";
import { primaryNavigationDestinationsSettingSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import { corpusSettingsSchema } from "@-/dev.improve.haniverse.lib.morpheme.corpus.settings";
import { customLevelPacksSchema } from "@-/dev.improve.haniverse.lib.level.custom";
import { schedulePronunciationBacklog } from "#schedule-pronunciation-backlog";
import { settingsChangedAnalyticsProperties } from "#settings-changed-analytics-properties";

// The settings columns that live on the singleton `user` row. The CRUD/identity
// columns are excluded so a settings patch can never rewrite `id`, timestamps,
// the soft-delete tombstone.
type UserSettingsColumn = Exclude<
  keyof typeof actorSchema.user.$inferInsert,
  "id" | "createdAt" | "updatedAt" | "deletedAt"
>;

/**
 * A partial patch of the actor's per-user settings columns (which now live on
 * the singleton `user` row). Any subset of the settings columns may be provided
 * — a single field (`{ hanziScript }`), a boolean toggle (`{ isToneColorsEnabled
 * }`), or a whole bag of tone-color columns from a preset — all go through the
 * same update.
 */
export type UserSettingsUpdate = Partial<
  Pick<typeof actorSchema.user.$inferInsert, UserSettingsColumn>
>;

/**
 * Unified mutation for writing any subset of the actor's per-user settings
 * columns on the singleton `user` row.
 *
 * Replaces the former per-field `useSet*Mutation` hooks: instead of one hook
 * per column, callers `mutate` a partial settings object and this runs a single
 * Drizzle `update` against the actor DB's singleton `user` row. The write syncs
 * across the user's devices like every other actor-DB change.
 */
export function useUpdateUserSettings() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  return useMutation({
    mutationFn: async (values: UserSettingsUpdate, { client }) => {
      if (values.customLevelPacks !== undefined) {
        customLevelPacksSchema.parse(values.customLevelPacks);
      }
      if (values.primaryNavigationDestinations !== undefined) {
        primaryNavigationDestinationsSettingSchema.parse(values.primaryNavigationDestinations);
      }
      if (values.corpusSettings !== undefined) {
        corpusSettingsSchema.parse(values.corpusSettings);
      }
      if (!traditionalCharactersEnabled && values.hanziScript === "traditional") {
        throw new Error("Traditional characters are not enabled for this account.");
      }
      const actorUserData = await client.fetchQuery(actorUserDataQueryOptions);
      // Key the update on the resolved user id — the same singleton row every
      // reader resolves (see `actorUserDataQueryFn`) — so the toggle and the
      // screen always agree.
      return actorDb(import.meta.location).transaction(async (tx) => {
        const user = await tx.query.user.findFirst({
          where: { id: actorUserData.user.id },
        });
        if (
          values.isPinyinFlashcardsEnabled === true &&
          user?.isPinyinFlashcardsEnabled === false
        ) {
          await schedulePronunciationBacklog(tx, new Date());
        }
        return tx
          .update(actorSchema.user)
          .set(values)
          .where(eq(actorSchema.user.id, actorUserData.user.id))
          .run();
      });
    },
    mutationKey: ["updateUserSettings"],
    onSuccess: (_result, values) => {
      captureAnalyticsEvent("settings_changed", settingsChangedAnalyticsProperties(values));
    },
  });
}
